// Components
import { Typography } from '@archieai/ui';
import { ShareButtons } from '../ShareButtons/ShareButtons';

// Utils
import { cn } from '../../utils/cn';

// Types
type ArticleHeroProps = {
  /** Article title */
  title: string;
  /** Article subtitle/description */
  subtitle?: string;
  /** Author name */
  author: string;
  /** Tags/categories displayed as pipe-separated string */
  tags: string;
  /** URL for sharing (defaults to current page) */
  shareUrl?: string;
  /** Additional className for custom styling */
  className?: string;
};

/**
 * ArticleHero - Hero section for article detail page
 * Displays title, subtitle, author, tags, and share buttons
 * 
 * Spacing from Figma:
 * - Title → Subtitle: 24px (spacing-24)
 * - Subtitle → Author: 32px (spacing-32)
 * - Author → Tags: 8px (spacing-8)
 * - Tags → Share: 32px (spacing-32)
 */
export const ArticleHero = ({
  title,
  subtitle,
  author,
  tags,
  shareUrl,
  className,
}: ArticleHeroProps) => {
  return (
    <header
      className={cn(
        'flex flex-col items-center text-center w-full',
        className
      )}
    >
      {/* Title & Subtitle Container - gap-24 */}
      <div className="flex flex-col gap-[var(--spacing-24)] w-full max-w-[804px]">
        <h1 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000 max-md:text-[32px] max-md:leading-[40px]">
          {title}
        </h1>
        {subtitle && (
          <p className="font-heading text-[20px] leading-[28px] font-light text-blue-1000">
            {subtitle}
          </p>
        )}
      </div>

      {/* Author, Tags & Share Buttons */}
      <div className="flex flex-col items-center gap-[var(--spacing-16)] pt-[32px]">
        <Typography as="p" variant="h4" className="text-blue-800">
          {author}
        </Typography>
        <Typography variant="x-small" className="text-blue-800">
          {tags}
        </Typography>
        <ShareButtons url={shareUrl} title={title} />
      </div>
    </header>
  );
};
